import styles from './SingleTable.scss';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BundlePricings } from 'src/hooks/useDCS';
import { Bundle } from 'src/services/cms/bundleTypes';
import { isBundleSelfServicePurchaseEnabled } from 'src/utils/BundlePricingUtils';
import { BundleCTA } from '../../../BundleCTA/BundleCTA';
import { PricingTableCell } from '../../PricingTableCell/PricingTableCell';
import { PricingTableGroup } from '../../PricingTableGroup/PricingTableGroup';
import { PricingTableHeadColumn } from '../../PricingTableHeadColumn/PricingTableHeadColumn';
import { PricingTableRowTitle } from '../../PricingTableRowTitle/PricingTableRowTitle';
import { FeaturesByCategories } from '../../types';

interface SingleTableProps {
  bundle: Bundle;
  categoriesWithFeatures: FeaturesByCategories;
  pricings?: BundlePricings;
  solutionTitle?: string;
}

export const SingleTable: React.FC<SingleTableProps> = ({
  bundle,
  categoriesWithFeatures,
  pricings,
  solutionTitle,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <table className={styles.table} role="menu">
        <caption className={styles.tableCaption}>
          <span>{t('pricing-detail-table.caption')}</span>
        </caption>

        <thead className={styles.tableHeadColumn}>
          <tr>
            <PricingTableHeadColumn
              title={bundle.title}
              description={bundle.description}
              image={bundle.image}
              key={bundle.id}
              id={bundle.id}
              colSpanCount={2}
              pricings={pricings}
            />
          </tr>
        </thead>

        {categoriesWithFeatures.map(category => (
          <PricingTableGroup title={category.title} key={category.id} colSpanCount={1}>
            {category.features.map(feature => {
              const value = feature.values.find(value => value.bundleId === bundle.id);
              const { keyedInRate, inPersonRate } = pricings || {};

              return (
                <tr key={feature.id} data-test="data-row">
                  <PricingTableRowTitle
                    title={feature.title}
                    description={feature.description}
                    productDetails={feature.product}
                  />
                  <PricingTableCell value={value} keyedInRate={keyedInRate} inPersonRate={inPersonRate} />
                </tr>
              );
            })}
          </PricingTableGroup>
        ))}
      </table>

      <div className={styles.tableButton} data-test="data-single-table-button">
        <BundleCTA
          checkoutLink={bundle.checkoutLink}
          dcsId={bundle.dcsId}
          isSelfServicePurchaseEnabled={isBundleSelfServicePurchaseEnabled(pricings)}
          shouldUseCheckoutLink={bundle.shouldUseCheckoutLink}
          solutionTitle={solutionTitle}
        />
      </div>
    </>
  );
};
